
import { useEffect, useRef, useState } from 'react';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

const badges = [
  {
    icon: "ri-shield-check-fill",
    title: "Organik Sertifikalı",
    description: "AB standartlarında üretim",
    stat: "100%",
    statLabel: "Organik",
    gradient: "from-brand-500 to-brand-700"
  },
  {
    icon: "ri-test-tube-fill",
    title: "Laboratuvar Testli",
    description: "Her parti bağımsız kontrol",
    stat: "3x",
    statLabel: "Test Aşaması",
    gradient: "from-brand-400 to-cyan-600"
  },
  {
    icon: "ri-heart-pulse-fill",
    title: "Pediatrist Onaylı",
    description: "Çocuklar için %100 güvenli",
    stat: "200+",
    statLabel: "Uzman Onayı",
    gradient: "from-amber-500 to-orange-500"
  },
  {
    icon: "ri-truck-fill",
    title: "Hızlı Teslimat",
    description: "Aynı gün kargo imkanı",
    stat: "24s",
    statLabel: "İçinde Kargoda",
    gradient: "from-rose-400 to-pink-500"
  },
  {
    icon: "ri-customer-service-2-fill",
    title: "7/24 Destek",
    description: "Uzman eczacı danışmanlığı",
    stat: "7/24",
    statLabel: "Canlı Destek",
    gradient: "from-brand-600 to-brand-800"
  }
];

function AnimatedCounter({ target }: { target: string }) {
  const [display, setDisplay] = useState('0');
  const ref = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const numericPart = target.replace(/[^0-9]/g, '');
          const num = parseInt(numericPart, 10);
          if (isNaN(num)) {
            setDisplay(target);
            return;
          }
          const duration = 1500;
          const steps = 40;
          const stepTime = duration / steps;
          let current = 0;
          const increment = num / steps;
          const interval = setInterval(() => {
            current += increment;
            if (current >= num) {
              setDisplay(target);
              clearInterval(interval);
            } else {
              const prefix = target.match(/^[^0-9]*/)?.[0] ?? '';
              const suffixPart = target.match(/[^0-9]*$/)?.[0] ?? '';
              setDisplay(`${prefix}${Math.floor(current)}${suffixPart}`);
            }
          }, stepTime);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [target]);

  return (
    <div ref={ref} className="text-xl sm:text-2xl font-bold text-white leading-none">
      {display}
    </div>
  );
}

export default function TrustBadges() {
  const titleReveal = useScrollReveal({ animation: 'fadeUp', delay: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const windowH = window.innerHeight;
      const start = windowH;
      const end = -rect.height;
      const progress = Math.min(1, Math.max(0, (start - rect.top) / (start - end)));
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative py-12 sm:py-16 md:py-20 overflow-hidden bg-gradient-to-b from-gray-50 to-white" ref={containerRef}>
      <div
        className="absolute top-0 left-1/4 w-48 sm:w-96 h-48 sm:h-96 bg-brand-100/30 rounded-full blur-3xl"
        style={{ transform: `translateY(${scrollProgress * -60}px) scale(${0.8 + scrollProgress * 0.4})` }}
      />
      <div
        className="absolute bottom-0 right-1/4 w-40 sm:w-80 h-40 sm:h-80 bg-amber-100/20 rounded-full blur-3xl"
        style={{ transform: `translateY(${scrollProgress * 40}px) scale(${0.8 + scrollProgress * 0.3})` }}
      />

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 relative z-10">
        <div ref={titleReveal.ref} className={`text-center mb-8 sm:mb-12 md:mb-16 ${titleReveal.className}`}>
          <div className="inline-flex items-center gap-2 bg-brand-50 border border-brand-200 rounded-full px-4 sm:px-5 py-1.5 sm:py-2 mb-4 sm:mb-5">
            <div className="w-2 h-2 bg-brand-500 rounded-full animate-pulse" />
            <span className="text-brand-700 text-[10px] sm:text-xs font-semibold tracking-wider uppercase">
              Neden Bizi Tercih Etmelisiniz?
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Güvenin <span className="text-brand-600">Adresi</span>
          </h2>
          <p className="text-gray-500 text-xs sm:text-sm max-w-xl mx-auto leading-relaxed px-2">
            Ailenizin sağlığı için en yüksek standartlarda üretim, test ve teslimat süreçleri
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-5">
          {badges.map((badge, index) => (
            <BadgeCard key={index} badge={badge} index={index} />
          ))}
        </div>

        <BottomTrustBar />
      </div>
    </section>
  );
}

function BadgeCard({ badge, index }: { badge: typeof badges[0]; index: number }) {
  const reveal = useScrollReveal({ animation: 'zoomIn', delay: index * 120 });

  return (
    <div ref={reveal.ref} className={`group relative ${reveal.className} ${index === 4 ? 'col-span-2 sm:col-span-1' : ''}`}>
      <div className="relative bg-white rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 border border-gray-100 hover:border-brand-200 transition-all duration-500 hover:shadow-xl hover:shadow-brand-100/50 hover:-translate-y-2 cursor-pointer overflow-hidden h-full">
        <div className={`absolute inset-0 bg-gradient-to-br ${badge.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl sm:rounded-2xl`} />

        <div className="relative z-10">
          <div className="mb-3 sm:mb-4">
            <div className={`text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r ${badge.gradient} bg-clip-text text-transparent group-hover:text-white transition-colors duration-500`}>
              <AnimatedCounter target={badge.stat} />
            </div>
            <div className="text-[9px] sm:text-[11px] text-gray-400 group-hover:text-white/70 font-medium uppercase tracking-wider mt-0.5 sm:mt-1 transition-colors duration-500">
              {badge.statLabel}
            </div>
          </div>

          <div className={`w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br ${badge.gradient} rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-white/20 transition-all duration-500 group-hover:scale-110`}>
            <i className={`${badge.icon} text-base sm:text-lg md:text-xl text-white`}></i>
          </div>

          <h3 className="font-bold text-gray-900 text-xs sm:text-sm mb-1 sm:mb-1.5 group-hover:text-white transition-colors duration-500">
            {badge.title}
          </h3>
          <p className="text-[10px] sm:text-xs text-gray-500 leading-relaxed group-hover:text-white/80 transition-colors duration-500">
            {badge.description}
          </p>
        </div>

        <div className={`absolute -bottom-8 -right-8 w-16 sm:w-24 h-16 sm:h-24 bg-gradient-to-br ${badge.gradient} rounded-full opacity-5 group-hover:opacity-20 group-hover:scale-150 transition-all duration-700`} />
      </div>
    </div>
  );
}

function BottomTrustBar() {
  const reveal = useScrollReveal({ animation: 'fadeUp', delay: 600 });

  return (
    <div ref={reveal.ref} className={`mt-8 sm:mt-10 md:mt-14 ${reveal.className}`}>
      <div className="bg-gradient-to-r from-brand-700 via-brand-600 to-brand-500 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 relative overflow-hidden">
        <div className="absolute inset-0 shimmer-bg" />

        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
          <div className="flex items-center gap-3 sm:gap-4 text-center md:text-left">
            <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 bg-white/20 rounded-lg sm:rounded-xl flex items-center justify-center backdrop-blur-sm flex-shrink-0">
              <i className="ri-award-fill text-lg sm:text-xl md:text-2xl text-white"></i>
            </div>
            <div>
              <div className="text-white font-bold text-sm sm:text-base md:text-lg">50.000+ Aile Bize Güveniyor</div>
              <div className="text-brand-100 text-[10px] sm:text-xs md:text-sm">Türkiye&apos;nin en güvenilir çocuk takviye gıda markası</div>
            </div>
          </div>
          <div className="flex items-center gap-3 sm:gap-6">
            <div className="flex -space-x-2 sm:-space-x-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full border-2 border-white overflow-hidden">
                  <img
                    src={`https://readdy.ai/api/search-image?query=portrait%20of%20a%20happy%20Turkish%20parent%20smiling%20warmly%2C%20professional%20headshot%2C%20clean%20background%2C%20natural%20lighting%2C%20friendly%20face&width=80&height=80&seq=trustface${i}&orientation=squarish`}
                    alt="Müşteri"
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
            <div className="flex items-center gap-0.5 sm:gap-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <i key={i} className="ri-star-fill text-amber-300 text-sm sm:text-base md:text-lg"></i>
              ))}
              <span className="text-white font-bold text-sm sm:text-base ml-1 sm:ml-2">4.9</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
